/**
 * Counter — numeric-only badge with overflow handling.
 *
 * Use when: showing a count of items (open incidents, unread notifications,
 * queue size). Caps at `max` (default 99) and renders `99+` for larger
 * values — Atlassian Badge / Primer CounterLabel pattern. For categorical
 * tags use `Pill`. For interactive chips use `Chip`.
 */
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

type CounterTone = 'neutral' | 'brand' | 'danger' | 'warning' | 'success';

const useStyles = makeStyles({
  counter: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: `0 ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  neutral: { backgroundColor: tokens.colorNeutralBackground3,      color: tokens.colorNeutralForeground1 },
  brand:   { backgroundColor: tokens.colorBrandBackground2,        color: tokens.colorBrandForeground1 },
  danger:  { backgroundColor: tokens.colorPaletteRedBackground1,   color: tokens.colorPaletteRedForeground1 },
  warning: { backgroundColor: tokens.colorPaletteYellowBackground1, color: tokens.colorPaletteYellowForeground1 },
  success: { backgroundColor: tokens.colorPaletteGreenBackground1, color: tokens.colorPaletteGreenForeground1 },
  large: {
    height: '24px',
    minWidth: '24px',
    fontSize: tokens.fontSizeBase300,
  },
});

interface CounterProps {
  value: number;
  /** Clamp value above this — rendered as "{max}+". Default 99. */
  max?: number;
  tone?: CounterTone;
  size?: 'medium' | 'large';
  /** Optional label for screen readers (e.g. "open incidents"). */
  'aria-label'?: string;
}

export function Counter({
  value,
  max = 99,
  tone = 'neutral',
  size = 'medium',
  'aria-label': ariaLabel,
}: CounterProps) {
  const styles = useStyles();
  const display = value > max ? `${max}+` : String(value);
  const sr = ariaLabel ? `${value} ${ariaLabel}` : `${value}`;

  return (
    <span
      className={mergeClasses(styles.counter, styles[tone], size === 'large' && styles.large)}
      role="status"
      aria-label={sr}
    >
      {display}
    </span>
  );
}
